import {
  getSiteTitle,
  getSiteDescription,
  getStructuredData,
  BASE_URL,
  type localeType,
  type translatorType,
  type structuredDataPageType,
} from '~/models/StructuredData'

export const useLocaleHeadConfig = () => {
  const { locale } = useI18n()
  const route = useRoute()
  const baseUrl = BASE_URL

  // useHead に関数を渡すことで、内部の locale.value や route.path の変更を自動追跡します
  useHead(() => {
    const currentLocale = locale.value as localeType
    const currentPath = route.path
    const isEn = currentLocale === 'en'

    // 言語ごとのデータを定義
    const siteTitle = getSiteTitle(currentLocale)

    const siteDesc = getSiteDescription(currentLocale)

    const ogLocale = isEn ? 'en_US' : 'ja_JP'

    const commonPath = currentPath.replace(/^\/en(?=\/|$)/, '') || '/'
    const jaUrl = `${baseUrl}${commonPath}`
    const enUrl = `${baseUrl}/en${commonPath === '/' ? '' : commonPath}`
    const canonicalUrl = isEn ? enUrl : jaUrl

    return {
      htmlAttrs: {
        lang: currentLocale,
      },
      title: siteTitle,
      link: [
        { rel: 'canonical', href: canonicalUrl },
        { rel: 'alternate', hreflang: 'ja-JP', href: jaUrl },
        { rel: 'alternate', hreflang: 'en-US', href: enUrl },
        { rel: 'alternate', hreflang: 'x-default', href: jaUrl },
      ],
      meta: [
        { name: 'description', content: siteDesc },
        { property: 'og:site_name', content: siteTitle },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:title', content: siteTitle },
        { property: 'og:description', content: siteDesc },
        { property: 'og:image', content: 'https://amahitotsu.com/OGP.png' },
        { property: 'og:locale', content: ogLocale },
        { property: 'og:locale:alternate', content: isEn ? 'ja_JP' : 'en_US' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: siteTitle },
        { name: 'twitter:description', content: siteDesc },
        { name: 'twitter:image', content: 'https://amahitotsu.com/OGP.png' },
      ],
    }
  })
}

// リッチリザルト表示のため、JSON-LD（構造化データ）をheadに出力
export const useStructuredData = (pageType: structuredDataPageType) => {
  const { locale, t } = useI18n()

  // 表示文言と重複する項目はロケールファイルから取得する
  const translate: translatorType = (key: string) => t(key)

  useHead(() => {
    const currentLocale = locale.value as localeType
    const structuredData = getStructuredData(currentLocale, translate, pageType)

    return {
      script: [
        {
          innerHTML: JSON.stringify(structuredData),
          type: 'application/ld+json',
        },
      ],
    }
  })
}
